/**
 * App callbacks setup.
 * Binds: home (play / continue / new game), nav (back / debug / settings), debug (load state).
 */

import { SaveStateManager } from '../../features/save-state'
import type { StoryEngine } from '../../features/story-engine'
import type { AppUI } from '../app-orchestrator'
import type { ScreenNavigationDependencies } from '../screen-navigation'

export interface AppCallbacksDependencies {
  storyEngine: StoryEngine
  ui: AppUI
  showGameScreen: (loadState: boolean, updateButtonsBeforeFade: boolean) => void
  showHomeScreen: () => void
  isTransitioning: { value: boolean }
}

/**
 * Setup all application callbacks.
 * Top-level flow: bind home → bind nav → bind debug.
 */
export function setupAppCallbacks(dependencies: AppCallbacksDependencies): void {
  const { storyEngine, ui, showGameScreen, showHomeScreen, isTransitioning } = dependencies
  bindHomeCallbacks({ storyEngine, ui, showGameScreen, showHomeScreen, isTransitioning })
  bindNavCallbacks({ ui, showHomeScreen })
  bindDebugCallbacks({ storyEngine, ui })
}

// --- Home screen (play / continue / new game) ---

function bindHomeCallbacks(deps: AppCallbacksDependencies): void {
  const { storyEngine, ui, showGameScreen, showHomeScreen, isTransitioning } = deps

  ui.homeScreen.setOnPlay(() => {
    SaveStateManager.clear()
    storyEngine.reset()
    showGameScreen(false, true)
  })

  ui.homeScreen.setOnContinue(() => {
    showGameScreen(true)
  })

  ui.homeScreen.setOnNewGame(() => {
    ui.confirmationModal.show(
      'Začít znovu?',
      'Spuštění nové hry vymaže veškerý uložený postup.',
      ['Zpět', 'Začít znovu'],
      () => {
        isTransitioning.value = true
        SaveStateManager.clear()
        storyEngine.reset()
        showGameScreen(false)
      },
      () => {},
    )
  })
}

// --- Nav (back / debug / settings) ---

function bindNavCallbacks(deps: { ui: AppUI; showHomeScreen: () => void }): void {
  deps.ui.gameNavigation.setOnBack(() => deps.showHomeScreen())
  deps.ui.gameNavigation.setOnChapter(() => deps.ui.chaptersModal.show())
  deps.ui.gameNavigation.setOnDebug(() => deps.ui.debugModal.show())
  deps.ui.gameNavigation.setOnSettings(() => deps.ui.settingsModal.show())
}

// --- Debug (load game state to scene) ---

const DEBUG_LOAD_SCENE_DELAY_MS = 500

function bindDebugCallbacks(deps: { storyEngine: StoryEngine; ui: AppUI }): void {
  deps.ui.debugModal.setOnLoadGameState((sceneIndex) => {
    deps.storyEngine.loadGameStateToScene(sceneIndex)
    setTimeout(() => deps.storyEngine.scrollToScene(sceneIndex), DEBUG_LOAD_SCENE_DELAY_MS)
  })
}
